import React, { useState } from 'react';
import { Box, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { Droppable } from 'react-beautiful-dnd';
import { PILLAR_ICON_FOR_KEY } from './pillarIcons';
import type { LearningPillar, PillarIcon } from './types';
import { MOBILE_SX, useIsMobileApp } from '../../../../../../mobile/mobileApp';

const PILLAR_ICON_OPTIONS: { key: PillarIcon; label: string }[] = [
  { key: 'onboard', label: 'Onboard' },
  { key: 'operate', label: 'Operate' },
  { key: 'perform', label: 'Perform' },
  { key: 'lead', label: 'Lead' }
];

// Remove column CTA — visible in edit mode for incomplete or empty
// columns. Simple red bordered button at the column footer (the only
// per-column CTA now that "Add New Program" has been removed).
const REMOVE_RED = '#DA2828'; // TBD if Dosen has a specific error red token
const RemoveColumnButton: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      width: '100%',
      pt: '10px',
      pb: '10px',
      pl: '12px',
      pr: '14px',
      bgcolor: 'transparent',
      border: `1px solid ${REMOVE_RED}`,
      borderRadius: '6px',
      color: REMOVE_RED,
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
      fontWeight: 600,
      lineHeight: '19.5px',
      cursor: 'pointer',
      transition: 'background-color 0.15s',
      '&:hover': { bgcolor: 'rgba(218, 40, 40, 0.08)' }
    }}
  >
    Remove
  </Box>
);

interface Props {
  pillar: LearningPillar;
  programCount: number;
  children?: React.ReactNode; // program cards
  editMode?: boolean; // Edit Framework mode — header becomes editable
  onPillarChange?: (changes: Partial<LearningPillar>) => void; // edit-mode draft updates
  onRemove?: () => void; // edit-mode Remove CTA — drops the pillar from drafts
  // Set to true after the admin attempts a Save Changes that has partial
  // columns (missing name or icon). Triggers red error styling on the
  // affected fields. Resets to false on successful save.
  showErrors?: boolean;
  // Elliott's Recommended Course cards for this pillar — pinned above the
  // program cards, outside the droppable (they aren't real programs yet).
  recommendations?: React.ReactNode;
  // The Assessments in this stage (Capability release), above the program
  // cards and outside the droppable: an Assessment's stage is set when it is
  // run, not by dragging its card.
  assessments?: React.ReactNode;
}

/**
 * A single Learning Pillar column — header (icon + name + count), card body,
 * and a pinned "Add New Program" CTA at the bottom.
 *
 * In Edit Framework mode, the header switches to an editable pill (icon
 * placeholder + text input + character count). Body and footer remain as-is.
 */
const PillarColumn: React.FC<Props> = ({
  pillar,
  programCount,
  children,
  editMode = false,
  onPillarChange,
  onRemove,
  showErrors = false,
  recommendations,
  assessments
}) => {
  const Icon = pillar.icon ? PILLAR_ICON_FOR_KEY[pillar.icon] : null;
  const isMobileApp = useIsMobileApp();

  // In edit mode, surface a Remove CTA for any column that's either empty
  // (no programs) or incomplete (missing name or icon). Complete columns
  // with programs in them have no Remove — admin must drain them first.
  const isIncomplete = !pillar.name.trim() || !pillar.icon;
  const showRemoveCTA = editMode && (programCount === 0 || isIncomplete);

  return (
    // Column wrapper — each column is exactly 25% of the viewport so 4 fit
    // across the screen at any time. A 5th (or the Edit Framework "Add new
    // column" tile) overflows to the right, triggering the nav arrow.
    // bg #1C1C1C, right border #2A2A2A, padding 20, gap 10.
    <Stack
      sx={{
        flex: '0 0 25%',
        minWidth: 0,
        height: '100%',
        // Tighter horizontal padding gives width back to the left panel.
        py: '14px',
        px: '8px',
        gap: '8px',
        // Transparent so the near-black page shows through behind the cards.
        // The column used to fill with #1C1C1C, which sat almost on top of the
        // #151515 tiles and the #2A2A2A card borders — flattening them into one
        // block. On the darker page the card borders read clearly, so each tile
        // stands alone. The right divider keeps the columns delineated.
        bgcolor: 'transparent',
        borderRight: '1px solid #2A2A2A',
        // Phone: one section per stage, full width, stacked down the page.
        [MOBILE_SX]: { flex: '1 1 auto', width: '100%', height: 'auto', p: 0, gap: '12px', borderRight: 'none' }
      }}
    >
      {editMode ? (
        <EditablePillarHeader
          pillar={pillar}
          showErrors={showErrors}
          onNameChange={(name) => onPillarChange?.({ name })}
          onIconChange={(icon) => onPillarChange?.({ icon })}
        />
      ) : (
        <ReadOnlyPillarHeader pillar={pillar} programCount={programCount} Icon={Icon} />
      )}

      {/* Column body — ONE scroll container holding Elliott's recommendations
          and the program cards, so the whole stack scrolls together rather than
          the recommendations staying frozen above a scrolling list. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          // 16px so the gap below the recommendation block matches the 16px
          // between the program cards beneath it (one consistent rhythm).
          gap: '16px',
          overflowY: 'auto',
          // Thin dark scrollbar to match the framework's horizontal scroll.
          scrollbarWidth: 'thin',
          scrollbarColor: '#565656 transparent',
          '&::-webkit-scrollbar': { width: '8px', height: '8px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#565656', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#666666' },
          // The page scrolls on the phone, not the column.
          [MOBILE_SX]: { flex: '0 0 auto', overflowY: 'visible', gap: '12px' }
        }}
      >
        {recommendations && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>{recommendations}</Box>
        )}

        {assessments && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', flexShrink: 0 }}>{assessments}</Box>
        )}

        {/* Droppable so cross-pillar DnD can move cards between columns. DnD is
            disabled in edit mode — you can't drag program cards while editing
            the column structure. */}
        <Droppable droppableId={pillar.id} isDropDisabled={editMode} ignoreContainerClipping>
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                flex: '1 0 auto',
                display: 'flex',
                flexDirection: 'column',
                // Roomier 16px spacing between stacked cards (was 6px) so each
                // tile has breathing room and reads as a separate object (PR #519).
                gap: '16px',
                // Subtle hover feedback when something is dragged over.
                bgcolor: snapshot.isDraggingOver ? 'rgba(247, 148, 32, 0.04)' : 'transparent',
                borderRadius: '6px',
                transition: 'background-color 0.15s'
              }}
            >
              {children}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>

        {/* A blank stage on the phone would read as still loading. */}
        {isMobileApp && !editMode && React.Children.count(children) === 0 && !recommendations && !assessments && (
          <Typography
            sx={{ py: '28px', textAlign: 'center', fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#868686' }}
          >
            Nothing in {pillar.name || 'this stage'} yet.
          </Typography>
        )}
      </Box>

      {/* Column footer — edit mode only. The per-column "Add New Program" CTA
          was removed: programs are added from the Elliott orb, or by publishing
          one of his recommendations. */}
      {editMode && showRemoveCTA && <RemoveColumnButton onClick={onRemove} />}
    </Stack>
  );
};

// Read-only header — Figma:
//   padding 16/20, radius 4, border 1px #868686 (Stroke-Border-border-highlight),
//   gap 8; icon 24x24 (or placeholder circle if null); title Inter 18/600
//   uppercase #FDFDFD; count pill bg #2A2A2A radius 2, text Inter 16/400 #868686.
const ReadOnlyPillarHeader: React.FC<{
  pillar: LearningPillar;
  programCount: number;
  Icon: React.FC<{ size?: number; color?: string }> | null;
}> = ({ pillar, programCount, Icon }) => (
  <Box
    sx={{
      // Slimmer, with the brandy frame + faint tint used on the side panels.
      border: '1px solid rgba(247, 148, 32, 0.45)',
      bgcolor: 'rgba(247, 148, 32, 0.06)',
      borderRadius: '4px',
      pl: '16px',
      pr: '16px',
      pt: '5px',
      pb: '5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }}
  >
    <Box
      sx={{
        width: 24,
        height: 24,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {Icon ? (
        <Icon size={24} color="#FDFDFD" />
      ) : (
        <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #FDFDFD' }} />
      )}
    </Box>
    <Typography
      sx={{
        color: '#FDFDFD',
        fontFamily: 'Inter, sans-serif',
        fontSize: 18,
        fontWeight: 600,
        textTransform: 'uppercase'
      }}
    >
      {pillar.name}
    </Typography>
    <Box
      sx={{
        bgcolor: '#2A2A2A',
        borderRadius: '2px',
        pl: '6px',
        pr: '6px',
        pt: '2px',
        pb: '2px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Typography
        sx={{
          color: '#868686',
          fontFamily: 'Inter, sans-serif',
          fontSize: 16,
          fontWeight: 400,
          lineHeight: 1
        }}
      >
        {programCount}
      </Typography>
    </Box>
  </Box>
);

// Editable header — Figma spec:
//   padding 24/16, radius 4, border 1px #B6B6B6 (Stroke/Border/border-contrast);
//   bg rgba(0,0,0,0.70); gap 8; icon slot 20x20 (clickable — opens picker
//   menu of users/org/document/systems; white circle placeholder when unset);
//   text input Inter 18/600 #FDFDFD with "Add title..." placeholder
//   (placeholder fades to transparent on focus so backspace doesn't appear
//   to no-op); character-count badge bg #2A2A2A radius 2.
const EditablePillarHeader: React.FC<{
  pillar: LearningPillar;
  showErrors: boolean;
  onNameChange: (name: string) => void;
  onIconChange: (icon: PillarIcon) => void;
}> = ({ pillar, showErrors, onNameChange, onIconChange }) => {
  const Icon = pillar.icon ? PILLAR_ICON_FOR_KEY[pillar.icon] : null;
  const [iconMenuAnchor, setIconMenuAnchor] = useState<HTMLElement | null>(null);
  const iconMenuOpen = Boolean(iconMenuAnchor);

  // Per-field error states for edit mode validation — only red if the
  // admin has already attempted a Save Changes with this field empty.
  // Default state stays neutral until they try to commit.
  const nameMissing = pillar.name.trim() === '';
  const iconMissing = pillar.icon === null;
  const showNameError = showErrors && nameMissing;
  const showIconError = showErrors && iconMissing;

  return (
    <Box
      sx={{
        bgcolor: 'rgba(0, 0, 0, 0.70)',
        // Red border ONLY after a failed Save Changes attempt with
        // this name still empty. Default state is the standard
        // border-contrast colour.
        border: `1px solid ${showNameError ? REMOVE_RED : '#B6B6B6'}`,
        borderRadius: '4px',
        pl: '24px',
        pr: '24px',
        pt: '16px',
        pb: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}
    >
      {/* Icon slot — clickable. Click to open the 4-option picker.
          Shows the chosen icon, or a white-circle placeholder when unset. */}
      <Box
        component="button"
        type="button"
        onClick={(e: React.MouseEvent<HTMLElement>) => setIconMenuAnchor(e.currentTarget)}
        aria-label="Choose stage icon"
        sx={{
          width: 28,
          height: 28,
          p: 0,
          border: 'none',
          bgcolor: 'transparent',
          borderRadius: '4px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flex: '0 0 auto',
          transition: 'background-color 0.15s',
          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' }
        }}
      >
        {Icon ? (
          <Icon size={20} color="#FDFDFD" />
        ) : (
          // Missing-icon placeholder. Red ring ONLY after a failed
          // Save Changes attempt; default state stays white.
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: `2px solid ${showIconError ? REMOVE_RED : '#FDFDFD'}`
            }}
          />
        )}
      </Box>

      <Menu
        anchorEl={iconMenuAnchor}
        open={iconMenuOpen}
        onClose={() => setIconMenuAnchor(null)}
        PaperProps={{
          sx: {
            mt: 1,
            bgcolor: '#1C1C1C',
            border: '1px solid #2A2A2A',
            borderRadius: '6px',
            // Icon-only menu — no labels per Cian. Items are 36x36 squares
            // arranged vertically (or could be horizontal — pick later if
            // requested).
            '& .MuiMenuItem-root': {
              p: '8px',
              minWidth: 36,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { bgcolor: '#262626' }
            }
          }
        }}
      >
        {PILLAR_ICON_OPTIONS.map((opt) => {
          const OptIcon = PILLAR_ICON_FOR_KEY[opt.key];
          const selected = pillar.icon === opt.key;
          return (
            <MenuItem
              key={opt.key}
              selected={selected}
              onClick={() => {
                onIconChange(opt.key);
                setIconMenuAnchor(null);
              }}
              aria-label={opt.label}
            >
              <OptIcon size={20} color={selected ? '#FDFDFD' : '#B6B6B6'} />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Inline editable name. Placeholder hides on focus so it doesn't
          appear unresponsive to backspace when the field is empty. */}
      <Box
        component="input"
        value={pillar.name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onNameChange(e.target.value)}
        placeholder="Add title..."
        sx={{
          flex: '1 1 0',
          minWidth: 0,
          bgcolor: 'transparent',
          border: 'none',
          outline: 'none',
          color: '#FDFDFD',
          fontFamily: 'Inter, sans-serif',
          fontSize: 18,
          fontWeight: 600,
          textAlign: 'center',
          '&::placeholder': { color: '#FDFDFD', opacity: 1 },
          '&:focus::placeholder': { opacity: 0 }
        }}
      />

      {/* Character count badge */}
      <Box
        sx={{
          bgcolor: '#2A2A2A',
          borderRadius: '2px',
          pl: '6px',
          pr: '6px',
          pt: '2px',
          pb: '2px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto'
        }}
      >
        <Typography
          sx={{
            color: '#868686',
            fontFamily: 'Inter, sans-serif',
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1
          }}
        >
          {pillar.name.length}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Phone: one chip per stage, in the stage header's look (icon, count, name), pinned over the board.
 * A tap scrolls the page to that stage's section; the stage on screen is lit. Four fit across;
 * more scroll sideways.
 */
export const PillarJumpRow: React.FC<{
  columns: Array<{ pillar: LearningPillar; count: number }>;
  activeId: string | null;
  onJump: (id: string) => void;
}> = ({ columns, activeId, onJump }) => (
  <Box
    role="navigation"
    aria-label="Jump to stage"
    sx={{
      display: 'flex',
      gap: '6px',
      overflowX: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' }
    }}
  >
    {columns.map(({ pillar, count }) => {
      const Icon = pillar.icon ? PILLAR_ICON_FOR_KEY[pillar.icon] : null;
      const on = pillar.id === activeId;
      const color = on ? '#FDFDFD' : '#B6B6B6';
      return (
        <Box
          key={pillar.id}
          component="button"
          type="button"
          aria-label={`Go to ${pillar.name || 'Untitled'}, ${count}`}
          aria-current={on ? 'true' : undefined}
          onClick={() => onJump(pillar.id)}
          sx={{
            flex: '1 0 auto',
            minWidth: 78,
            minHeight: 46,
            px: '8px',
            py: '6px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            // The stage header's frame and tint; the stage on screen in full brandy.
            border: `1px solid ${on ? '#F79420' : 'rgba(247, 148, 32, 0.45)'}`,
            bgcolor: on ? 'rgba(247, 148, 32, 0.16)' : 'rgba(247, 148, 32, 0.06)',
            borderRadius: '4px',
            color,
            transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {Icon ? (
              <Icon size={16} color={color} />
            ) : (
              <Box sx={{ width: 13, height: 13, borderRadius: '50%', border: `2px solid ${color}` }} />
            )}
            <Box
              component="span"
              sx={{
                bgcolor: '#2A2A2A',
                borderRadius: '2px',
                px: '5px',
                py: '1px',
                color: '#868686',
                fontSize: 11,
                lineHeight: '14px'
              }}
            >
              {count}
            </Box>
          </Box>
          <Box
            component="span"
            sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1, textTransform: 'uppercase', whiteSpace: 'nowrap' }}
          >
            {pillar.name || 'Untitled'}
          </Box>
        </Box>
      );
    })}
  </Box>
);

export default PillarColumn;
